import React, { useCallback, useEffect, useState } from 'react';

import { Button, Card, Grid } from '@mui/material';
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import { DashboardModal } from '@uppy/react';
import localforage from 'localforage';
import _ from 'lodash';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import CropperDialog from '../../../components/CropperDialog';
import Previewer from '../../../components/Previewer';
import ControllerTextField from '../../../components/react-hook-form-wrappers/controller-text-field';
import { getAdAwsS3UploadKit, removeS3Objects } from '../../../store/actions';

require('@uppy/core/dist/style.css');
require('@uppy/dashboard/dist/style.css');

const createUppy = ({
  getAdAwsS3UploadKitAction,
  removeS3ObjectsAction,
  getValues,
  setValue,
}) => {
  return new Uppy({
    restrictions: {
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['image/png', 'image/jpeg', 'image/jpg'],
    },
  })
    .use(AwsS3, {
      getUploadParameters(file) {
        return getAdAwsS3UploadKitAction({
          fileName: `${file.name}.${file.extension}`,
        }).then((awsS3UploadKit) => {
          setValue('temp.awsS3UploadKit', awsS3UploadKit);
          return {
            method: 'PUT',
            url: awsS3UploadKit.original.putOptions.url,
          };
        });
      },
    })
    .on('complete', (result) => {
      if (result.successful.length === 1) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setValue('temp.localOriginal', {
            url: reader.result,
            type: result.successful[0].type,
          });
        };
        reader.readAsDataURL(result.successful[0].data);
      } else if (result.failed.length > 0) {
        setValue('temp', undefined);
      }
    })
    .on('file-removed', (file, reason) => {
      if (reason === 'removed-by-user' && file.progress.uploadComplete) {
        const newAwsS3UploadKit = getValues('temp.awsS3UploadKit');
        setValue('temp', undefined);
        if (!_.isEmpty(newAwsS3UploadKit)) {
          removeS3ObjectsAction({
            objectsToRemove: [
              {
                deleteOptions: newAwsS3UploadKit.original.deleteOptions,
              },
            ],
            type: 'ad',
          });
        }
      }
    });
};

const AD_TYPE_CROP = {
  portrait: { aspectRatio: 7 / 9, title: 'Crop portrait image' },
  banner: { aspectRatio: 3 / 1, title: 'Crop banner image' },
};

const AdUploader = ({
  control,
  isEditing,
  getValues,
  setValue,
  awsS3UploadKit,
  initialRequestOptions,
  showOriginalImage,
  watchAdTypeField,
  disabled,
}) => {
  const dispatch = useDispatch();
  const adType = useWatch({ control, name: watchAdTypeField });
  const cropConfig = AD_TYPE_CROP[adType] || AD_TYPE_CROP.banner;

  const getAdAwsS3UploadKitAction = useCallback(
    (payload) => dispatch(getAdAwsS3UploadKit(payload)),
    [dispatch]
  );

  const removeS3ObjectsAction = useCallback(
    ({ objectsToRemove, type }) => {
      return dispatch(removeS3Objects({ objectsToRemove, type }));
    },
    [dispatch]
  );

  const [uppy] = useState(() =>
    createUppy({
      getValues,
      setValue,
      getAdAwsS3UploadKitAction,
      removeS3ObjectsAction,
    })
  );
  const [openUploader, setOpenUploader] = useState(false);

  const localOriginalUrl = useWatch({
    control,
    name: 'localOriginalUrl',
  });

  const croppedImageUrl = useWatch({
    control,
    name: 'croppedImageUrl',
  });

  const originalFileName = useWatch({
    control,
    name: 'originalFileName',
  });

  useEffect(() => {
    return () => {
      if (uppy) {
        uppy.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCroppedImagesToUploadedImages = (type) => {
    const uploadedImages = getValues('uploadedImages') || [];
    const cropped = awsS3UploadKit?.cropped;

    if (!cropped) {
      return;
    }

    const existingCroppedKeys = new Set(Object.keys(cropped));
    const images = _.filter(
      uploadedImages,
      (image) => !existingCroppedKeys.has(image.key)
    );

    Object.keys(cropped).forEach((key) => {
      images.push({
        key,
        name: cropped[key].fileName,
        deleteOptions: cropped[key].deleteOptions,
        type,
      });
    });

    const nextCroppedImageUrl = {
      ...(getValues('croppedImageUrl') || {}),
      cropped: cropped.default?.getOptions?.url,
    };

    setValue('croppedImageUrl', nextCroppedImageUrl);

    setValue('uploadedImages', images, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleDashboardModalClose = () => {
    const temp = getValues('temp');
    if (temp && temp.localOriginal && temp.awsS3UploadKit) {
      const uploadedImages = getValues('uploadedImages') || [];
      if (!_.isEmpty(uploadedImages)) {
        removeS3ObjectsAction({
          objectsToRemove: [...uploadedImages],
          type: 'ad',
        });
      }

      setValue(
        'uploadedImages',
        [
          {
            key: 'original',
            name: temp.awsS3UploadKit.original.fileName,
            type: temp.localOriginal.type,
            deleteOptions: temp.awsS3UploadKit.original.deleteOptions,
          },
        ],
        { shouldDirty: true, shouldTouch: true }
      );
      setValue('originalFileName', temp.awsS3UploadKit.original.fileName, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue('localOriginalUrl', temp.localOriginal.url);
      setValue('croppedImageUrl', {});

      localforage.setItem(temp.awsS3UploadKit.original.fileName, {
        imageSrc: temp.localOriginal.url,
        __timestamp: Date.now(),
      });
    }

    setValue('temp', undefined);
    setOpenUploader(false);
  };

  const originalUrl =
    localOriginalUrl || awsS3UploadKit?.original?.getOptions?.url;

  const originalPreviewUrl =
    localOriginalUrl || initialRequestOptions?.original?.url;

  return (
    <>
      {showOriginalImage && (
        <Grid
          container
          item
          xs={12}
          md={4}
          direction="row"
          justifyContent="center"
          style={{ marginBottom: 24 }}
        >
          <Grid item xs={12}>
            <Previewer
              imageUrl={originalPreviewUrl}
              widthThreshold={2160}
              tooltip="Original image"
            />
          </Grid>
        </Grid>
      )}
      <Grid
        component={Card}
        container
        item
        xs={12}
        md={7}
        gap={3}
        style={{ padding: 24 }}
      >
        <Grid container item spacing={3}>
          <Grid item md={8} xs={12}>
            <ControllerTextField
              control={control}
              label="File Name"
              name="originalFileName"
              disabled
              required={false}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Button
              color="primary"
              type="button"
              variant="contained"
              fullWidth
              onClick={() => setOpenUploader(true)}
              disabled={disabled}
            >
              Open Uploader
            </Button>
          </Grid>
          <DashboardModal
            uppy={uppy}
            showLinkToFileUploadResult={false}
            showRemoveButtonAfterComplete
            proudlyDisplayPoweredByUppy={false}
            note="Please choose an image (*.jpg, *.jpeg, *.png)"
            style={{ zIndex: 99999 }}
            open={openUploader}
            onRequestClose={handleDashboardModalClose}
          />
        </Grid>
        <Grid item xs={12}>
          <CropperDialog
            key={adType}
            aspectRatio={cropConfig.aspectRatio}
            currentImage={initialRequestOptions?.cropped}
            generatePreview={{ maxWidth: 100, quality: 0.1 }}
            isSrcNewUpload={!!localOriginalUrl || !isEditing}
            maxWidth={3600}
            onCropSuccess={(preview) => {
              addCroppedImagesToUploadedImages('image/jpeg');
              if (preview) {
                setValue('preview', preview, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }
            }}
            awsS3UploadKit={awsS3UploadKit?.cropped}
            originalFileName={originalFileName}
            src={originalUrl}
            croppedImageUrl={croppedImageUrl?.cropped}
            title={cropConfig.title}
            tooltip="Ad"
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AdUploader;
